import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { DateField, DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import CloseIcon from "@mui/icons-material/Close";
import EventFilledIcon from "@mui/icons-material/Event";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  ChangeSolvingServiceReport,
  ServiceReport,
  ServiceReportFile,
  ServiceReportFileTypeCode,
  ServiceReportStatusCode,
  ServiceReportsState,
} from "../models/ServiceReportsModel";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import moment from "moment";
import ServiceReportUploadFiles from "./ServiceReportUploadFile";
import { CurrencyCode } from "../../../common/models/CommonModels";
import {
  changeSolvingServiceReport,
  createSolvingServiceReport,
  getServiceReports,
} from "../customerMachineServiceReportsSlice";
import { dt } from "../utils/dt";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import useTranslation from "../../../shared/hooks/useTranslation";

export interface SolvingServiceReportDialogProps {
  open: boolean;
  onClose: () => void;
  machineId: string;
}

const SolvingServiceReportDialog = ({ open, onClose, machineId }: SolvingServiceReportDialogProps) => {
  useRedesignSetup();
  const { serviceReport, serviceReportsQuery } = useAppSelector<ServiceReportsState>(
    (state: any) => state.customerServiceReports
  );

  const serviceReportNumber = serviceReport?.number || undefined;
  const isStatusCodeInProgress = serviceReport?.statusCode === ServiceReportStatusCode.inProgress;
  const solvedReportFiles = serviceReport?.files.filter(
    (x) => x.typeCode === ServiceReportFileTypeCode.solvingReportDocument
  );
  const [files, setFiles] = useState<ServiceReportFile[] | null>(solvedReportFiles || []);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const isPhone = useMediaQuery("(max-width:768px)");

  const handlerCreateFile = (files: ServiceReportFile[] | null) => {
    setFiles(files);
  };

  const dispatch = useAppDispatch();

  const {
    register,
    watch,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChangeSolvingServiceReport>();

  const closeDate = watch("closeDate");

  const onSubmit: SubmitHandler<ChangeSolvingServiceReport> = (data: ChangeSolvingServiceReport) => {
    handleCreateServiceReport(data);
  };

  const handleCreateServiceReport = async (model: ChangeSolvingServiceReport) => {
    model.closeDate = model.closeDate || moment().toDate();
    model.files = files;
    model.cost = { id: CurrencyCode.eur, value: Number(model.cost?.value || 0) };
    if (isStatusCodeInProgress) {
      await dispatch(createSolvingServiceReport({ model, serviceReportNumber }));
    } else {
      await dispatch(changeSolvingServiceReport({ model, serviceReportNumber }));
    }
    onClose();
    await dispatch(getServiceReports({ machineId: machineId, query: serviceReportsQuery }));
  };

  useEffect(() => {
    if (serviceReport) {
      const changeServiceReport: ChangeSolvingServiceReport = {
        solvingDescription: serviceReport.solvingDescription || "",
        closeDate: serviceReport.closeDate || undefined,
        cost: serviceReport.cost || {},
        files: serviceReport.files || null,
      };
      reset(changeServiceReport);
    } else {
      reset({} as ServiceReport);
    }
  }, [serviceReport?.number, reset]);

  const { translate } = useTranslation("myMachinery");
  const { translate: tCommon } = useTranslation("common");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isPhone}
      PaperProps={{
        sx: {
          borderRadius: isPhone ? 0 : dt.radiusSm,
          overflow: "hidden",
          fontFamily: dt.font,
        },
      }}
    >
      {/* Green accent bar */}
      <Box sx={{ height: "4px", background: `linear-gradient(90deg, ${dt.serviceReports.dialogGreenAccent}, #2ECC71)` }} />

      <DialogTitle
        sx={{
          fontFamily: dt.font,
          fontWeight: 700,
          fontSize: "18px",
          color: dt.textPrimary,
          textAlign: "center",
          pt: 3,
          pb: 1,
        }}
      >
        {isStatusCodeInProgress
          ? translate("markServiceReportAsSolvedTitle")
          : translate("editSolvedReport")}
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{ position: "absolute", right: 14, top: 18, color: dt.textMuted }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ px: 3, pb: 2, fontFamily: dt.font }}>
        <form id="changeSolvingServiceReport" onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, mt: 1 }}>
            <Box>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
                <Box>
                  <InputLabel sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 500 }}>
                    {translate("closeDate")}
                  </InputLabel>
                  <FormControl fullWidth size="small">
                    <Controller
                      control={control}
                      name="closeDate"
                      rules={{
                        validate: (value) => {
                          if (value && serviceReport?.openDate) {
                            if (moment(value) > moment()) {
                              return translate("closeDateFuture");
                            }
                            return (
                              moment(value) >= moment(serviceReport.openDate) ||
                              translate("mustBeGreaterThan", {
                                date: moment(serviceReport.openDate).format("DD.MM.YYYY, HH:mm"),
                              })
                            );
                          }
                        },
                      }}
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DatePicker
                            {...field}
                            value={moment(field.value)}
                            onChange={(value) => field.onChange(value?.toDate())}
                            format="DD.MM.YYYY"
                            minDate={moment(serviceReport?.openDate)}
                            maxDate={moment()}
                            disableFuture
                            views={["year", "month", "day"]}
                            open={isCalendarOpen}
                            onOpen={() => setIsCalendarOpen(true)}
                            onClose={() => setIsCalendarOpen(false)}
                            slotProps={{
                              textField: {
                                size: "small",
                                InputProps: {
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() => setIsCalendarOpen(true)}
                                        sx={{
                                          color: dt.serviceReports.dialogGreenAccent,
                                          "&:hover": { backgroundColor: "rgba(39,174,96,0.06)" },
                                        }}
                                      >
                                        <EventFilledIcon />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                },
                              },
                              popper: {
                                sx: {
                                  "& .MuiPickersDay-root": {
                                    "&.Mui-selected": {
                                      backgroundColor: dt.serviceReports.dialogGreenAccent,
                                      "&:hover": { backgroundColor: dt.serviceReports.dialogGreenAccent },
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </FormControl>
                </Box>
                <Box>
                  <InputLabel sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 500 }}>
                    {tCommon("common.time")}
                  </InputLabel>
                  <FormControl fullWidth size="small">
                    <Controller
                      control={control}
                      name="closeDate"
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DateField
                            {...field}
                            value={moment(closeDate)}
                            onChange={(value) => field.onChange(value?.toDate())}
                            format="HH:mm"
                            size="small"
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </FormControl>
                </Box>
              </Box>
              <FormHelperText error>{errors.closeDate?.message}</FormHelperText>
            </Box>

            <Box>
              <InputLabel sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 500 }}>
                {translate("closingReport")}
              </InputLabel>
              <TextField
                fullWidth
                multiline
                minRows={6}
                {...register("solvingDescription")}
                error={!!errors.solvingDescription}
                helperText={errors.solvingDescription?.message}
                inputProps={{ maxLength: 2000 }}
                sx={{
                  "& .MuiInputBase-input": { fontFamily: dt.fontMono, fontSize: "14px", resize: "vertical" },
                }}
              />
            </Box>

            <Box>
              <InputLabel sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: 500 }}>
                {translate("costEur")}
              </InputLabel>
              <TextField
                type="number"
                fullWidth
                size="small"
                variant="outlined"
                {...register("cost.value", {
                  required: tCommon("forms.required"),
                  max: { value: 100000, message: translate("maxValue100k") },
                  min: { value: 0, message: translate("minValue0") },
                })}
                error={!!errors.cost?.value}
                helperText={errors.cost?.value?.message}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box sx={{ fontFamily: dt.fontMono, fontWeight: 600, color: dt.textSecondary, fontSize: "14px" }}>
                        &euro;
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{ "& .MuiInputBase-input": { fontFamily: dt.fontMono } }}
              />
              <input type="hidden" {...register("cost.id", { value: CurrencyCode.eur })} />
            </Box>

            <Box>
              <ServiceReportUploadFiles
                files={solvedReportFiles || []}
                onChange={handlerCreateFile}
                typeCode={ServiceReportFileTypeCode.solvingReportDocument}
                isCreate={isStatusCodeInProgress}
              />
            </Box>
          </Box>
        </form>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3, px: 3 }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            minWidth: "160px",
            fontFamily: dt.font,
            fontWeight: 500,
            textTransform: "none",
            borderRadius: dt.radiusSm,
            borderColor: dt.cardBorder,
            color: dt.textSecondary,
            "&:hover": { borderColor: dt.textSecondary },
          }}
        >
          {tCommon("buttons.cancel")}
        </Button>

        <Button
          form="changeSolvingServiceReport"
          type="submit"
          variant="contained"
          sx={{
            minWidth: "160px",
            background: `linear-gradient(135deg, ${dt.serviceReports.dialogGreenAccent} 0%, #2ECC71 100%)`,
            borderRadius: dt.radiusPill,
            fontFamily: dt.font,
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "0 2px 8px rgba(39,174,96,0.25)",
            "&:hover": {
              background: `linear-gradient(135deg, #229954 0%, ${dt.serviceReports.dialogGreenAccent} 100%)`,
            },
          }}
        >
          {translate("saveAsSolved")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SolvingServiceReportDialog;
